import type { AuthResponse, UserDTO } from '@copilot/shared';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, use, useCallback, useEffect, useMemo, useState, useSyncExternalStore, type ReactNode } from 'react';
import { authApi } from '../api/auth';
import { refreshSession } from '../api/client';
import { sessionStore } from './session';

interface AuthContextValue {
  /** 'loading' until the initial refresh-cookie check has finished. */
  status: 'loading' | 'ready';
  user: UserDTO | null;
  login(email: string, password: string): Promise<void>;
  signup(email: string, password: string, name: string): Promise<void>;
  logout(): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function applyAuth(res: AuthResponse) {
  sessionStore.set({ accessToken: res.accessToken, user: res.user });
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const session = useSyncExternalStore(sessionStore.subscribe, sessionStore.get);
  const [status, setStatus] = useState<'loading' | 'ready'>('loading');

  // Restore the session from the httpOnly refresh cookie on app load.
  useEffect(() => {
    let cancelled = false;
    void refreshSession().finally(() => {
      if (!cancelled) setStatus('ready');
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Drop cached data whenever the user changes (logout, session expiry, switch).
  const userId = session?.user.id ?? null;
  useEffect(() => {
    queryClient.clear();
  }, [userId, queryClient]);

  const login = useCallback(async (email: string, password: string) => {
    applyAuth(await authApi.login(email, password));
  }, []);

  const signup = useCallback(async (email: string, password: string, name: string) => {
    applyAuth(await authApi.signup(email, password, name));
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      sessionStore.set(null);
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user: session?.user ?? null, login, signup, logout }),
    [status, session, login, signup, logout],
  );
  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthContextValue {
  const ctx = use(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

/** The signed-in user; only call inside routes wrapped by <RequireAuth>. */
export function useUser(): UserDTO {
  const { user } = useAuth();
  if (!user) throw new Error('useUser called without a signed-in user');
  return user;
}
